import React, { useMemo } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Text,
  Link,
  Header,
  Button,
  List,
  ListItem,
  ListItemKey,
  ListItemValue,
  Loader,
  Fields,
  FieldGroup,
  StripeInput,
  Label,
  Input,
  Base,
} from '@pet-notify/ui'
import {
  useElements,
  useStripe,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js'
import { Stripe, StripeCardNumberElement } from '@stripe/stripe-js'
import { useMutation } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'

import type { CheckoutInput } from 'services/orders'
import { usePlans } from 'hooks/plans'
import { useCheckout } from 'hooks/orders'
import { formatMoney } from '@pet-notify/formatters'
import PaymentMethodIcons from 'components/PaymentMethodIcons'
import ErrorMessage from 'components/ErrorMessage'
import style from './style.module.css'
import Address from 'components/Address'

function createStripeToken({
  stripe,
  cardNumberElement,
}: {
  stripe: Stripe
  cardNumberElement: StripeCardNumberElement
}) {
  return stripe.createToken(cardNumberElement).then(({ token, error }) => {
    if (token) {
      return token
    } else if (error) {
      throw error.message
    } else {
      throw 'Something went wrong'
    }
  })
}

type Props = {
  state: Partial<CheckoutInput>
  onComplete: () => void
}

type FormData = {
  zip: string
}

function Confirmation({ state, onComplete }: Props) {
  const stripe = useStripe()
  const elements = useElements()
  const checkoutMutation = useCheckout()
  const { register, handleSubmit } = useForm<FormData>()

  const createTokenMutation = useMutation(createStripeToken)

  const onSubmit = handleSubmit(({ zip }) => {
    if (!stripe || !elements) {
      return
    }

    const cardNumberElement = elements.getElement(CardNumberElement)

    if (!cardNumberElement) {
      return
    }

    createTokenMutation.mutate(
      { stripe, cardNumberElement },
      {
        onSuccess: (token) => {
          checkoutMutation.mutate(
            { ...state, payment: { zip, token: token.id } },
            {
              onSuccess: onComplete,
            },
          )
        },
      },
    )
  })

  const plansQuery = usePlans()

  const plan = useMemo(() => {
    if (plansQuery.data && state.planInterval) {
      return plansQuery.data.find((p) => p.interval === state.planInterval)
    }
  }, [plansQuery, state.planInterval])

  if (plansQuery.isLoading) {
    return <Loader />
  }

  return (
    <>
      <Header mb='6' size='large'>
        Intellitag Activation &amp; Plan Confirmation
      </Header>
      <div className={style.headerWithLink}>
        <Header size='medium' as='h3'>
          Account summary
        </Header>
        <Link as={RouterLink} to='/redeem'>
          Edit account
        </Link>
      </div>

      <List>
        <ListItem>
          <ListItemKey>Name</ListItemKey>
          <ListItemValue>
            {state.firstName} {state.lastName}
          </ListItemValue>
        </ListItem>
        <ListItem>
          <ListItemKey>Email</ListItemKey>
          <ListItemValue>{state.email}</ListItemValue>
        </ListItem>
      </List>

      <Base className={style.headerWithLink} mb='5'>
        <Header size='medium' as='h3'>
          Shipping summary
        </Header>

        <Link as={RouterLink} to='/redeem/shipping'>
          Edit shipping
        </Link>
      </Base>

      <List>
        <ListItem>
          <Address>{state.shippingAddress}</Address>
        </ListItem>
      </List>

      <Base className={style.headerWithLink} mb='5'>
        <Header size='medium' as='h3'>
          Order summary
        </Header>

        <Link as={RouterLink} to='/redeem/intellitags'>
          Edit order
        </Link>
      </Base>

      <List>
        {state.order?.intellitagOrderItems?.map(({ size, name, phone }) => (
          <ListItem key={name}>
            <ListItemKey>
              <Header size='small' as='h4' mb='2'>
                Intellitag
              </Header>
              <Text mb='1'>
                <strong>Size:</strong> {size}
              </Text>
              <Text mb='1'>
                <strong>Name:</strong> {name}
              </Text>
              {phone && (
                <Text>
                  <strong>Phone:</strong> {phone}
                </Text>
              )}
            </ListItemKey>
            <ListItemValue>{formatMoney(0)}</ListItemValue>
          </ListItem>
        ))}

        <ListItem>
          <ListItemKey>Shipping</ListItemKey>
          <ListItemValue>{formatMoney(0)}</ListItemValue>
        </ListItem>

        <ListItem>
          <ListItemKey>Total</ListItemKey>
          <ListItemValue>{formatMoney(0)}</ListItemValue>
        </ListItem>
      </List>

      <div className={style.headerWithLink}>
        <Header size='medium' as='h3'>
          Intellitag with pet alarms monitoring plan
        </Header>
        <Link as={RouterLink} to='/redeem/plan'>
          Edit plan
        </Link>
      </div>

      {plan && (
        <List>
          <ListItem>
            <ListItemKey>
              Paid {plan.interval === 'year' ? 'Annually' : 'Monthly'}
            </ListItemKey>
            <ListItemValue>
              {plan.interval === 'year' ? (
                <>{formatMoney(Math.floor(plan.amount / 12) / 100)} per month</>
              ) : (
                <>{formatMoney(plan.amount / 100)} per month</>
              )}
            </ListItemValue>
          </ListItem>
        </List>
      )}

      <Header as='h2' mb='4' size='medium'>
        Payment information
      </Header>
      {plan && (
        <Text mb='4'>
          <strong>{formatMoney(plan.amount / 100)}</strong> is due today for
          your Pet Notify monitoring plan with Intellitag and pet emergency
          alarms. Unlimited household pets. No commitments. Cancel anytime.
        </Text>
      )}

      <form onSubmit={onSubmit}>
        <Fields>
          <FieldGroup>
            <Label>
              Credit card number
              <StripeInput as={CardNumberElement} />
            </Label>
          </FieldGroup>

          <FieldGroup className={style.sideBySideFieldGroup}>
            <Label>
              Expiration (mm/yy)
              <StripeInput as={CardExpiryElement} />
            </Label>
            <Label>
              Security code (CVV)
              <StripeInput as={CardCvcElement} />
            </Label>
          </FieldGroup>

          <FieldGroup>
            <Label htmlFor='zip'>ZIP code</Label>
            <Input
              className={style.zipInput}
              id='zip'
              {...register('zip', { required: true })}
            />
          </FieldGroup>
          <PaymentMethodIcons />

          {createTokenMutation.isError && (
            <ErrorMessage error={createTokenMutation.error} />
          )}

          {checkoutMutation.isError && (
            <ErrorMessage error={checkoutMutation.error} />
          )}
        </Fields>
        {checkoutMutation.isLoading || createTokenMutation.isLoading ? (
          <Loader />
        ) : (
          <Button type='submit' mb='4'>
            Confirm payment
          </Button>
        )}
      </form>

      <Text mb='3' className={style.text}>
        In confirming payment you agree to our{' '}
        <Link href='https://www.petnotify.com/terms-of-use'>Terms of Use</Link>,
        and{' '}
        <Link href='https://www.petnotify.com/privacy-policy'>
          Privacy Statement
        </Link>
        . You may modify or cancel your plan at anytime.
      </Text>

      <Text className={style.text}>
        Have a question? Email us at{' '}
        <Link href='mailto:support@petnotify.com'>support@petnotify.com</Link>.
      </Text>
    </>
  )
}

export default Confirmation
